import { ReactNode } from 'react';
import type { NsColumnProps } from '@britishgasenergy/react-nucleus/dist/nuc-components/NsColumn/NsColumn';
import { NsImage, NsColumn } from '@britishgasenergy/react-nucleus';

import { NsTimeline } from 'components/common/ns-timeline/NsTimeline';
import { NsTimelineEvent } from 'components/common/ns-timeline/NsTimelineEvent';
import { useHasSmartMeter } from 'components/direct-sales/quote/hooks/useHasSmartMeter';
import { useEvTariffInformation } from 'hooks/useEvTariffInformation';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsLandmark } from 'components/common/ns-landmark/NsLandmark';
import { CrossSellOffer } from 'components/direct-sales/order-complete/components/CrossSellOffer';
import { CrossSellOfferCaveats } from 'components/direct-sales/order-complete/components/CrossSellOfferCaveats';
import { usePaymentMethod } from 'hooks/usePaymentMethod';
import { useMeterType } from 'hooks/useMeterType';
import { useJaguar } from '../hooks/useJaguar';

import jaguarStraightThumbUp from 'assets/images/jaguar-straight-thumb-up-1-1-crop.png';
import { NsPill } from 'components/common/ns-pill/NsPill';

export const testId = 'orderCompleteContentTestId';
type WrapperProps = {
  children: ReactNode;
};

const Wrapper = ({ children }: WrapperProps) => {
  return <>{children}</>;
};

export const OrderCompleteContent = () => {
  const { isPrePay } = usePaymentMethod();
  const { hasElectricitySmartMeter } = useHasSmartMeter();
  const { showEvTariffInformation } = useEvTariffInformation();
  const { hasAnyTOUMeter, hasAnyPrepayMeter } = useMeterType();

  const { isJaguar, isDesktop, columnRatio } = useJaguar();
  return (
    <div data-testid={testId}>
      <NsLandmark
        type="hillside"
        heading="Thanks for joining British Gas"
        pill={
          <NsPill colour="navy" icon="success-solid">
            Complete
          </NsPill>
        }
        ratio="4x3"
      >
        <p>
          You&apos;ve made a great choice. You&apos;ll get an email to confirm
          everything in the next few minutes.
        </p>
      </NsLandmark>
      <NsPanel>
        <NsColumn hockeypitch={columnRatio as NsColumnProps['hockeypitch']}>
          {isJaguar && !isDesktop && <NsImage src={jaguarStraightThumbUp} />}
          <NsTimeline type="standard" heading="What happens next">
            <NsTimelineEvent
              status="completed"
              heading="Order placed"
              wrapper={Wrapper}
            >
              <p>
                We&apos;ll contact your current supplier to arrange everything.
                It normally takes 3-5 days to switch suppliers.
              </p>
            </NsTimelineEvent>
            <NsTimelineEvent
              status="inprogress"
              heading="Check your inbox for our Welcome Pack!"
              wrapper={Wrapper}
            >
              <p>
                Once final checks are complete, we will send you a welcome
                email. This has all the details about your chosen tariff, terms
                and conditions, cooling off period, start date, and how to
                activate your online account. If it hasn&apos;t arrived in the
                next 24 hours, just let us know.
              </p>
            </NsTimelineEvent>
            {isPrePay && hasAnyTOUMeter && (
              <NsTimelineEvent
                status="pending"
                heading="Topping up"
                testId="prepay-tou"
                wrapper={Wrapper}
              >
                We&apos;ll be sending you a new smart card(s). Please keep
                topping up as you do now until it arrives – we&apos;ll let you
                know when it&apos;s on the way. Once we&apos;re supplying your
                home, we&apos;ll also let you know how to top up online or
                through our app.
              </NsTimelineEvent>
            )}

            {isPrePay && hasAnyPrepayMeter && (
              <NsTimelineEvent
                status="pending"
                heading="Topping up"
                testId="prepay-traditional"
                wrapper={Wrapper}
              >
                We&apos;ll be sending you a new top up device. Please keep
                topping up as you do now until it arrives – we&apos;ll let you
                know when it&apos;s on the way.
              </NsTimelineEvent>
            )}

            {!isPrePay && (
              <NsTimelineEvent
                status="pending"
                heading="Your first meter reading"
                wrapper={Wrapper}
              >
                If we need a meter reading from you, we&apos;ll get in touch.
              </NsTimelineEvent>
            )}
            {showEvTariffInformation && hasElectricitySmartMeter && (
              <NsTimelineEvent
                status="pending"
                heading="Switch to our EV tariff"
                wrapper={Wrapper}
              >
                <p>
                  Once we&apos;ve switched your supply we&apos;ll connect to
                  your smart meter. It can take about 2 weeks to get everything
                  set up. Then you&apos;ll be able to switch to our EV tariff
                  through your online account.
                </p>
              </NsTimelineEvent>
            )}
            {showEvTariffInformation && !hasElectricitySmartMeter && (
              <>
                <NsTimelineEvent
                  status="pending"
                  heading="Book your smart meter"
                  wrapper={Wrapper}
                >
                  <p>
                    Once we&apos;ve switched your supply, we&apos;ll get all the
                    details about your meter and update your account - this can
                    take about 2 weeks. Then you&apos;ll be able to book a smart
                    meter appointment through your online account, at no extra
                    cost.
                  </p>
                </NsTimelineEvent>
                <NsTimelineEvent
                  status="pending"
                  heading="Switch to our EV tariff"
                  wrapper={Wrapper}
                >
                  <p>
                    After your smart meter is fitted, we&apos;ll connect it to
                    our system. Then you can switch to our EV tariff through
                    your online account.
                  </p>
                </NsTimelineEvent>
              </>
            )}
          </NsTimeline>
          {isJaguar && isDesktop && <NsImage src={jaguarStraightThumbUp} />}
        </NsColumn>
      </NsPanel>
      <CrossSellOffer />
      <CrossSellOfferCaveats />
    </div>
  );
};

export default OrderCompleteContent;
